import React, { useState, useRef, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Play, Info, ChevronDown, ChevronUp, Video } from 'lucide-react';

const WhatsAppTutorial: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);

    /** Fires a custom event so GuidedTour knows the video was played */
    const handleVideoPlay = useCallback(() => {
        window.dispatchEvent(new CustomEvent('onboarding-video-played'));
    }, []);

    return (
        <Card 
            className="border-2 border-emerald-100 shadow-sm overflow-hidden mb-8 animate-fadeIn" 
            id="whatsapp-tutorial-container"
            data-tour="whatsapp-tutorial"
        >
            {/* Header / Clickable Area */}
            <div
                className={`px-6 py-4 cursor-pointer transition-all duration-300 ${isOpen ? 'bg-gradient-to-r from-emerald-500 to-green-600' : 'bg-gradient-to-r from-emerald-50 to-green-50 hover:from-emerald-100 hover:to-green-100'}`}
                onClick={() => setIsOpen(!isOpen)}
                id="whatsapp-tutorial-header"
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={`${isOpen ? 'bg-white/20' : 'bg-gradient-to-r from-emerald-500 to-green-600'} p-2 rounded-lg backdrop-blur-sm transition-colors`}>
                            {isOpen ? (
                                <Play className="h-5 w-5 text-white fill-current" />
                            ) : (
                                <Video className="h-5 w-5 text-white" />
                            )}
                        </div>
                        <div>
                            <span className={`text-lg font-bold tracking-tight ${isOpen ? 'text-white' : 'text-emerald-900'}`}>
                                Tutorial de Conexión
                            </span>
                            <p className={`${isOpen ? 'text-emerald-50' : 'text-emerald-700'} text-xs font-normal mt-0.5`}>
                                Guía paso a paso en video
                            </p>
                        </div>
                    </div>
                    <div>
                        {isOpen ? (
                            <ChevronUp className="h-6 w-6 text-white" />
                        ) : (
                            <ChevronDown className="h-6 w-6 text-emerald-600" />
                        )}
                    </div>
                </div>
            </div>

            {/* Collapsible Content */}
            <div
                className={`transition-all duration-500 ease-in-out ${isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}
            >
                <CardContent className="p-0 bg-gray-900 relative aspect-video group">
                    {/* Video Player */}
                    {isOpen && (
                        <video
                            ref={videoRef}
                            id="whatsapp-tutorial-video"
                            className="w-full h-full object-cover"
                            controls
                            preload="metadata"
                            autoPlay={true}
                            onPlay={handleVideoPlay}
                            onTimeUpdate={(e) => {
                                // If user watched at least 5 seconds, consider it "played" for the tour
                                if ((e.target as HTMLVideoElement).currentTime > 5) {
                                    handleVideoPlay();
                                }
                            }}
                        >
                            <source src="https://storage.googleapis.com/reveca-whatsapp-media/tutorials/conexion%20Whatsapp%20verificaci%C3%B3n.mp4" type="video/mp4" />
                            Tu navegador no soporta la reproducción de videos.
                        </video>
                    )}

                    {/* Overlay informativo */}
                    <div className="absolute top-4 left-4 right-4 bg-black/60 backdrop-blur-md p-3 rounded-xl flex items-start gap-3 pointer-events-none transition-opacity duration-300 group-hover:opacity-0 opacity-100">
                        <Info className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <p className="text-white text-xs sm:text-sm leading-snug">
                            Recomendamos ver este tutorial completo antes de iniciar el proceso con Gupshup para asegurar una conexión exitosa.
                        </p>
                    </div>
                </CardContent>
            </div>
        </Card>
    );
};

export default WhatsAppTutorial;
